import { useState } from 'react';
import { api, getToken } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, date, dateTime, titleCase } from '../lib/format.ts';
import { BylawNotices, Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';

interface Run {
  id: string;
  period_starts_on: string;
  period_ends_on: string;
  issued_on: string;
  due_on: string;
  status: 'draft' | 'issued' | 'cancelled';
  statement_count: number;
  total_due_minor: number;
  created_at: string;
  issued_at: string | null;
}

interface RunDetail extends Run {
  statements: {
    id: string;
    statement_number: string;
    member_number: string;
    member_name: string;
    account_number: string;
    status: string;
    opening_balance_minor: number;
    closing_balance_minor: number;
    bucket_current_minor: number;
    bucket_30_minor: number;
    bucket_60_minor: number;
    bucket_90_plus_minor: number;
    message_count: number;
  }[];
  checks: { aging_foots: boolean; statements_not_footing: number };
}

/** Default to last calendar month, which is what a monthly cycle wants. */
function lastMonth(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

export function StatementsPage() {
  const { currency, atLeast } = useSession();
  const defaults = lastMonth();

  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [dueOn, setDueOn] = useState('');
  const [openRun, setOpenRun] = useState<string | null>(null);

  const runs = useApi<{ runs: Run[] }>('/v1/statement-runs');
  const detail = useApi<RunDetail>(openRun ? `/v1/statement-runs/${openRun}` : null);

  const generate = useMutation(async () =>
    api<Run>('/v1/statement-runs', {
      method: 'POST',
      body: {
        period_starts_on: from,
        period_ends_on: to,
        issued_on: to,
        due_on: dueOn || undefined,
      },
    }),
  );
  const issue = useMutation(async (id: string) =>
    api(`/v1/statement-runs/${id}/issue`, { method: 'POST' }),
  );
  const cancel = useMutation(async (id: string) =>
    api(`/v1/statement-runs/${id}/cancel`, { method: 'POST' }),
  );

  async function doGenerate() {
    const r = await generate.run();
    if (r) {
      runs.reload();
      setOpenRun(r.id);
    }
  }

  async function doIssue(id: string) {
    if (!window.confirm('Issue these statements? They become final and members can see them.')) return;
    if (await issue.run(id)) {
      runs.reload();
      detail.reload();
    }
  }

  async function doCancel(id: string) {
    if (!window.confirm('Discard this draft run? Nothing will have been sent.')) return;
    if (await cancel.run(id)) {
      runs.reload();
      setOpenRun(null);
    }
  }

  async function openPdf(statementId: string) {
    // The PDF endpoint needs the bearer token, so it is fetched and handed to
    // the browser as a blob rather than linked to directly.
    const res = await fetch(`/v1/statements/${statementId}.pdf`, {
      headers: { authorization: `Bearer ${getToken()}` },
    });
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Statements</h1>
      <p className="muted small" style={{ marginTop: 0, marginBottom: 18 }}>
        A run is generated as a draft first. Nothing reaches a member, and no transaction is
        claimed, until it is issued — so a bad batch can be thrown away rather than explained.
      </p>

      {/* Whatever the club's own document says about when money is due.
          Issuing statements is the moment it starts mattering. */}
      <BylawNotices surface="statement.run" />

      <ErrorBanner error={generate.error} />
      <ErrorBanner error={issue.error} />
      <ErrorBanner error={cancel.error} />

      {atLeast('controller') && (
        <Card title="Generate a Run">
          <div className="row" style={{ alignItems: 'flex-end' }}>
            <label className="field" style={{ width: 170, marginBottom: 0 }}>
              <span>Period from</span>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="field" style={{ width: 170, marginBottom: 0 }}>
              <span>Period to</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label className="field" style={{ width: 170, marginBottom: 0 }}>
              <span>Payment due</span>
              <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
              <div className="hint">Defaults to 15 days.</div>
            </label>
            <button onClick={doGenerate} disabled={generate.busy} className="gold">
              {generate.busy ? 'Generating…' : 'Generate draft'}
            </button>
          </div>
        </Card>
      )}

      <Card title="Runs" flush>
        {runs.loading ? (
          <div style={{ padding: 16 }}>
            <Loading />
          </div>
        ) : !runs.data || runs.data.runs.length === 0 ? (
          <Empty>No statement runs yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Period</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Status</th>
                <th className="num">Statements</th>
                <th className="num">Total due</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {runs.data.runs.map((r) => (
                <tr key={r.id}>
                  <td>
                    <a
                      href="#run"
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenRun(openRun === r.id ? null : r.id);
                      }}
                    >
                      {date(r.period_starts_on)} – {date(r.period_ends_on)}
                    </a>
                  </td>
                  <td className="muted">{date(r.issued_on)}</td>
                  <td className="muted">{date(r.due_on)}</td>
                  <td>
                    <Status
                      tone={
                        r.status === 'issued' ? 'good' : r.status === 'draft' ? 'warning' : 'neutral'
                      }
                    >
                      {titleCase(r.status)}
                    </Status>
                  </td>
                  <td className="num">{r.statement_count}</td>
                  <td className="num">
                    <Money minor={r.total_due_minor} currency={currency} />
                  </td>
                  <td className="num">
                    <button className="secondary small" onClick={() => setOpenRun(r.id)}>
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {openRun && detail.data && (
        <>
          <div className="tiles">
            <Tile label="Statements" value={detail.data.statements.length} />
            <Tile
              label="Total due"
              value={money(detail.data.total_due_minor, currency)}
              note={`${date(detail.data.period_starts_on)} – ${date(detail.data.period_ends_on)}`}
            />
            {/*
              Surfaced before the Issue button, not after. The database makes a
              non-footing statement impossible; this proves it to the person
              about to send 400 of them.
            */}
            <Tile
              label="Aging check"
              value={detail.data.checks.aging_foots ? 'Foots' : 'MISMATCH'}
              tone={detail.data.checks.aging_foots ? 'good' : 'critical'}
              note={
                detail.data.checks.aging_foots
                  ? 'every footer equals its own total'
                  : `${detail.data.checks.statements_not_footing} statements disagree`
              }
            />
            <Tile
              label="Status"
              value={titleCase(detail.data.status)}
              note={detail.data.issued_at ? dateTime(detail.data.issued_at) : 'not yet issued'}
              tone={detail.data.status === 'issued' ? 'good' : 'warning'}
            />
          </div>

          {detail.data.status === 'draft' && atLeast('controller') && (
            <Card>
              <div className="actions">
                <button
                  onClick={() => doIssue(detail.data!.id)}
                  disabled={issue.busy || !detail.data.checks.aging_foots}
                  className="gold"
                >
                  {issue.busy ? 'Issuing…' : `Issue ${detail.data.statements.length} statements`}
                </button>
                <button
                  className="secondary"
                  onClick={() => doCancel(detail.data!.id)}
                  disabled={cancel.busy}
                >
                  Discard draft
                </button>
                <span className="muted small">
                  Once issued, statements are final. A later adjustment appears on the next one.
                </span>
              </div>
            </Card>
          )}

          <Card title="Statements in This Run" flush>
            <table className="data">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Member</th>
                  <th className="num">Forward</th>
                  <th className="num">Current</th>
                  <th className="num">30</th>
                  <th className="num">60</th>
                  <th className="num">90+</th>
                  <th className="num">Due</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {detail.data.statements.map((s) => (
                  <tr key={s.id}>
                    <td className="mono">{s.statement_number}</td>
                    <td>
                      {s.member_name} <span className="muted mono">{s.member_number}</span>
                      {s.message_count > 0 && (
                        <span className="muted small"> · {s.message_count} notices</span>
                      )}
                    </td>
                    <td className="num muted">{money(s.opening_balance_minor, currency)}</td>
                    <td className="num">{money(s.bucket_current_minor, currency)}</td>
                    <td className="num">{money(s.bucket_30_minor, currency)}</td>
                    <td className="num">
                      {Number(s.bucket_60_minor) ? (
                        <span className="neg">{money(s.bucket_60_minor, currency)}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">
                      {Number(s.bucket_90_plus_minor) ? (
                        <span className="neg">{money(s.bucket_90_plus_minor, currency)}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num" style={{ fontWeight: 650 }}>
                      {money(s.closing_balance_minor, currency)}
                    </td>
                    <td className="num">
                      <button className="secondary small" onClick={() => openPdf(s.id)}>
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </>
  );
}
